import { apiClient } from "./apiClient";

const BASE = "/loyalty/public";

export interface BusinessListItem {
  _id: string;
  slug: string;
  businessName: string;
  businessType?: string;
  logo?: string;
  rating: number;
  reviewCount: number;
  serviceCategories: string[];
  /** Number of H3 hex cells (area of operation) */
  coverageHexCount?: number;
  offerCount: number;
  branchCount?: number;
}

export interface BusinessOffer {
  _id: string;
  title: string;
  description?: string;
  images?: string[];
  category?: string;
  offerTypeCode?: string;
  offerPrice?: number;
  originalPrice?: number;
  validFrom?: string;
  validTo?: string;
}

export interface ShopProduct {
  _id: string;
  title: string;
  description?: string;
  images?: string[];
  price: number;
  hasOffer?: boolean;
  offerPrice?: number;
  inStock?: boolean;
  category?: string;
}

export interface BusinessDetail {
  _id: string;
  slug?: string;
  businessDetails: {
    businessName: string;
    businessType?: string | { id?: string; name?: string };
    address?: string;
  };
  businessInfo: {
    businessLogo?: string;
    businessImages?: string[];
    description?: string;
    specialties?: string[];
    contactPhone?: string;
    storeLocation?: { coordinates: [number, number] };
    operatingHours?: Record<string, { open?: string; close?: string; isOpen?: boolean }>;
    rating?: number;
    reviewCount?: number;
    tagline?: string;
    coverImage?: string;
    websiteUrl?: string;
    whatsappNumber?: string;
    socialLinks?: { instagram?: string; facebook?: string; youtube?: string };
    videoUrl?: string;
    achievements?: string[];
    faqs?: { question: string; answer: string }[];
  };
  serviceCategories: string[];
  coverageHexagons?: string[];
  coveragePlan?: { planName?: string; hexCount?: number; isAllKerala?: boolean };
  offers: BusinessOffer[];
  offerCount: number;
  branches?: Branch[];
  reviews?: Review[];
  products?: ShopProduct[];
}

export interface Branch {
  _id: string;
  branchName: string;
  location?: {
    coordinates?: [number, number]; // [lng, lat]
    address?: string;
    city?: string;
    district?: string;
    state?: string;
    pincode?: string;
  };
  contactNumber?: string;
  contactEmail?: string;
  operatingHours?: {
    day: string;
    open: string;
    close: string;
    isOpen: boolean;
  }[];
}

export interface Review {
  _id: string;
  rating: number;
  comment?: string;
  images?: string[];
  createdAt: string;
  publicUserId?: {
    _id: string;
    name?: string;
    avatar?: string;
  };
}

export interface CategoryItem {
  name: string;
  count: number;
}

export interface BusinessesResponse {
  data: BusinessListItem[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export interface BusinessDetailResponse {
  data: BusinessDetail;
}

export interface CategoriesResponse {
  data: CategoryItem[];
}

export async function getBusinesses(params: {
  category?: string;
  /** User coordinates — backend resolves H3 cell for hex-based listing */
  lat?: number;
  lng?: number;
  search?: string;
  sort?: string;
  page?: number;
  limit?: number;
}): Promise<BusinessesResponse> {
  const searchParams: Record<string, string> = {};
  if (params.category) searchParams.category = params.category;
  if (params.lat != null) searchParams.lat = String(params.lat);
  if (params.lng != null) searchParams.lng = String(params.lng);
  if (params.search) searchParams.search = params.search;
  if (params.sort) searchParams.sort = params.sort;
  if (params.page != null) searchParams.page = String(params.page);
  if (params.limit != null) searchParams.limit = String(params.limit);
  const res = await apiClient.get<{ items: BusinessListItem[]; pagination: BusinessesResponse["pagination"] }>(
    `${BASE}/businesses`,
    searchParams
  );
  if (!res.success || res.data === undefined) {
    throw new Error(res.message || "Failed to fetch businesses");
  }
  const d = res.data as { items?: BusinessListItem[]; pagination?: BusinessesResponse["pagination"] };
  return {
    data: d.items ?? [],
    pagination: d.pagination ?? { page: 1, limit: 12, total: 0, pages: 0 },
  };
}

export async function getBusinessBySlug(slug: string): Promise<BusinessDetail> {
  const res = await apiClient.get<BusinessDetail>(`${BASE}/businesses/${encodeURIComponent(slug)}`);
  if (!res.success || res.data === undefined) {
    throw new Error(res.message || "Business not found");
  }
  return Array.isArray(res.data) ? (res.data[0] as BusinessDetail) : (res.data as BusinessDetail);
}

export async function getBusinessCategories(): Promise<CategoryItem[]> {
  const res = await apiClient.get<CategoryItem[] | { data: CategoryItem[] }>(`${BASE}/businesses/categories`);
  if (!res.success || res.data === undefined) {
    throw new Error(res.message || "Failed to fetch categories");
  }
  const d = res.data;
  return Array.isArray(d) ? d : (d as { data: CategoryItem[] }).data ?? [];
}
